import {Alert, Box, Button, CircularProgress, Stack, Typography, useTheme} from "@mui/material";
import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import backgroundImage from "../../assets/background.svg";
import tsIcon from "../../assets/ts.png";
import useLogin from "../../hooks/useLogin";
import {useIdentityAuth} from "../../context/IdentityAuthContext";
import {useAuthPermissions} from "../../hooks/useAuthPermissions";
import styles from "./Acceso.module.css";

const AUTH_ALLOWED_ROLES = process.env.REACT_APP_AUTH_ALLOWED_ROLES.split(",");

const errorMessages = {
  oidc_callback_failed: "Error en la autenticación con OpenID Connect",
  unauthorized: "No tienes permisos para acceder a esta página"
};

const Acceso = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [urlError, setUrlError] = useState("");
  const [errorCode, setErrorCode] = useState("");

  const {login, loading, error, clearError} = useLogin();
  const {isLoading, isAuthenticated} = useIdentityAuth();
  const navigate = useNavigate();
  const {verifyRole} = useAuthPermissions();

  // Manejar errores de la URL
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam && errorMessages[errorParam]) {
      setUrlError(errorMessages[errorParam]);
      setErrorCode(errorParam);
    }
  }, [searchParams]);

  // Redirigir si ya está autenticado Y tiene el rol correcto
  useEffect(() => {
    if (isAuthenticated && verifyRole(AUTH_ALLOWED_ROLES)) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate, verifyRole]);

  const handleLogin = async () => {
    const forceLogin = errorCode === "unauthorized" || errorCode === "oidc_callback_failed";
    await login("/dashboard", { forceLogin });
  };

  const handleClearError = () => {
    if (error) clearError();
    if (urlError) {
      setUrlError("");
      setErrorCode("");
    }
  };

  return (
    <Box className={styles.container} style={{backgroundImage: `url(${backgroundImage})`}}>
      <Stack
        className={styles.card}
        style={{
          "--backgroundColor": theme.palette.colors.background
        }}
      >
        {/* Logo o título */}
        <Stack spacing={1} direction={"column"} className={styles.titleHeader}>
          <Typography component="h1" variant="h4" className={styles.title} color={theme.palette.text.primary}>
            Acceso
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inicia sesión
          </Typography>
        </Stack>

        {/* Contenido del login */}
        <Stack direction={"column"} spacing={3} width={"100%"} alignItems="center">
          {/* Mensaje de error de URL */}
          {urlError && (
            <Alert severity="error" sx={{width: "100%"}} onClose={handleClearError}>
              {urlError}
            </Alert>
          )}

          {/* Mensaje de error de login */}
          {error && (
            <Alert severity="error" sx={{width: "100%"}} onClose={handleClearError}>
              {error}
              {error.includes("configuración") || error.includes("REACT_APP") ? (
                <Box sx={{mt: 1, fontSize: "0.875rem"}}>
                  <strong>Instrucciones:</strong>
                  <br />
                  1. Crea un archivo <code>.env</code> en la carpeta <code>app/</code>
                  <br />
                  2. Copia el contenido de <code>.env.example</code>
                  <br />
                  3. Configura las variables OIDC con tus datos
                  <br />
                  4. Reinicia el servidor con <code>npm start</code>
                </Box>
              ) : null}
            </Alert>
          )}

          {/* Botón OpenID Connect */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleLogin}
            disabled={loading || isLoading || error.includes("REACT_APP")}
            startIcon={<img src={tsIcon} alt="Teamsystem" style={{width: 24, height: 24}} />}
            size="large"
            className={styles.button}
            sx={{
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 500
            }}
          >
            {loading || isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : errorCode === "unauthorized" ? (
              "Intentar con otro usuario"
            ) : (
              "Iniciar sesión con Teamsystem ID"
            )}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Acceso;
